﻿/*
 * Purpose: Manage phone number state, country selection, and validation logic.
 * Boundary: Hook only; owns state, effects, and handlers; no JSX rendering.
 * Invariants:
 * - Input remains disabled until a country is selected; readonly if selector disabled.
 * - Validation uses regex pattern per selected country.
 * - Dropdown lists only enabled countries when selector is active.
 * - ASCII only; no Unicode characters in comments or strings.
 * Rollback: phonenumber-hooks.202610281006.js
 */

// Imports
import { useState, useEffect, useMemo, useRef } from 'react';

// Constants: Supported countries and patterns (national number length after dial code)
// ZA pinned at top, then A-Z. trunkPrefix = country uses 0 as national trunk dialling prefix.
const COUNTRY_DATA = [
  // --- Pinned ---
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: 'za', pattern: /^\d{9}$/, group: 'pinned', aliases: ['SA', 'RSA'], trunkPrefix: true },
  // --- A-Z ---
  { code: 'AO', name: 'Angola', dialCode: '+244', flag: 'ao', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: 'au', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: 'be', pattern: /^\d{8,9}$/, trunkPrefix: true },
  { code: 'BW', name: 'Botswana', dialCode: '+267', flag: 'bw', pattern: /^\d{7,8}$/, trunkPrefix: false },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: 'ca', pattern: /^\d{10}$/, trunkPrefix: false },
  { code: 'CY', name: 'Cyprus', dialCode: '+357', flag: 'cy', pattern: /^\d{8}$/, trunkPrefix: false },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268', flag: 'sz', pattern: /^\d{7,8}$/, trunkPrefix: true },
  { code: 'FR', name: 'France', dialCode: '+33', flag: 'fr', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: 'de', pattern: /^\d{10,11}$/, trunkPrefix: true },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: 'gr', pattern: /^\d{10}$/, trunkPrefix: true },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: 'ie', pattern: /^\d{7,9}$/, trunkPrefix: true },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: 'il', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: 'it', pattern: /^\d{9,10}$/, trunkPrefix: true },
  { code: 'LS', name: 'Lesotho', dialCode: '+266', flag: 'ls', pattern: /^\d{8}$/, trunkPrefix: false },
  { code: 'MW', name: 'Malawi', dialCode: '+265', flag: 'mw', pattern: /^\d{7,9}$/, trunkPrefix: true },
  { code: 'MU', name: 'Mauritius', dialCode: '+230', flag: 'mu', pattern: /^\d{7,8}$/, trunkPrefix: false },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258', flag: 'mz', pattern: /^\d{8,9}$/, trunkPrefix: true },
  { code: 'NA', name: 'Namibia', dialCode: '+264', flag: 'na', pattern: /^\d{7,8}$/, trunkPrefix: true },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: 'nl', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: 'nz', pattern: /^\d{8,10}$/, trunkPrefix: true },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: 'pt', pattern: /^\d{9}$/, trunkPrefix: false },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: 'qa', pattern: /^\d{7,8}$/, trunkPrefix: false },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: 'es', pattern: /^\d{9}$/, trunkPrefix: false },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: 'se', pattern: /^\d{7,9}$/, trunkPrefix: true },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: 'ch', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: 'ae', pattern: /^\d{9}$/, aliases: ['UAE'], trunkPrefix: false },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: 'gb', pattern: /^\d{10}$/, aliases: ['UK', 'Britain', 'England'], trunkPrefix: true },
  { code: 'US', name: 'United States', dialCode: '+1', flag: 'us', pattern: /^\d{10}$/, aliases: ['USA', 'America'], trunkPrefix: false },
  { code: 'ZM', name: 'Zambia', dialCode: '+260', flag: 'zm', pattern: /^\d{9}$/, trunkPrefix: true },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', flag: 'zw', pattern: /^\d{9}$/, trunkPrefix: true },
];

// Helper: Strip non-digits
function sanitizeInput(raw) {
    return raw.replace(/\D/g, '');
}

// Helper: Detect country from cleaned digits (longest dial code match first)
function detectCountry(digits, sortedCountries) {
    for (const country of sortedCountries) {
        const dialDigits = country.dialCode.replace(/\D/g, '');
        if (digits.startsWith(dialDigits)) {
            return { country, nationalPart: digits.slice(dialDigits.length) };
        }
    }
    return null;
}

// Module-level: COUNTRY_DATA sorted by dial code length descending for detection
const SORTED_BY_DIAL_LENGTH = [...COUNTRY_DATA].sort((a, b) => {
    const lenDiff = b.dialCode.length - a.dialCode.length;
    if (lenDiff !== 0) return lenDiff;
    if (a.code === 'US') return -1;
    if (b.code === 'US') return 1;
    return 0;
});

// Hook usePhoneNumber:
// Tracks country selection, phone input value, and validity
export function usePhoneNumber({
  enableCountrySelector = true,
  enabledCountries = COUNTRY_DATA.map(c => c.code),
  defaultCountry = 'ZA',
  onChange = () => {},
  initialValue = '',
}) {
  // States: Selection, input, and validity
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const defaultC = COUNTRY_DATA.find(c => c.code === defaultCountry);
    if (initialValue) {
      const digits = initialValue.replace(/\D/g, '');
      const detected = detectCountry(digits, SORTED_BY_DIAL_LENGTH);
      if (detected) {
        if (defaultC && detected.country.dialCode === defaultC.dialCode) return defaultC;
        return detected.country;
      }
    }
    return defaultC || COUNTRY_DATA[0];
  });

  const [phoneNumber, setPhoneNumberInternal] = useState(() => {
    if (initialValue) {
      const digits = initialValue.replace(/\D/g, '');
      const detected = detectCountry(digits, SORTED_BY_DIAL_LENGTH);
      return detected ? detected.nationalPart : digits;
    }
    return '';
  });

  const [isValid, setIsValid] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Ref: Lock detection after first auto-switch to prevent re-detection on subsequent digits
  const detectionLockedRef = useRef(false);

  // Derived: Countries enabled by prop
  const enabledCountryList = useMemo(
    () => (enableCountrySelector ? COUNTRY_DATA.filter(c => enabledCountries.includes(c.code)) : []),
    [enableCountrySelector, enabledCountries]
  );

  // Derived: Countries sorted by dial code length for auto-detection (longest first, US preferred over CA for +1)
  const sortedForDetection = useMemo(() =>
    [...enabledCountryList].sort((a, b) => {
      const lenDiff = b.dialCode.length - a.dialCode.length;
      if (lenDiff !== 0) return lenDiff;
      if (a.code === 'US') return -1;
      if (b.code === 'US') return 1;
      return 0;
    }),
    [enabledCountryList]
  );

  // Derived: Search-filtered countries for dropdown display
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return enabledCountryList;
    const term = searchTerm.toLowerCase();
    return enabledCountryList.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.dialCode.includes(term) ||
      c.code.toLowerCase().includes(term) ||
      (c.aliases && c.aliases.some(a => a.toLowerCase().includes(term)))
    );
  }, [enabledCountryList, searchTerm]);

  // Handlers: Sanitize, detect, decide, set
  // Three paths:
  // 1. International (+ in value or state): detect country, switch on match, lock.
  //    The + is kept in state until detection resolves so keystroke context survives.
  // 2. Trunk (0 prefix, no +): strip trunk 0, treat as national, lock.
  // 3. Plain digits: progressive detect with 3+ national digit confidence threshold.
  //    After first switch, detection locks — no more country jumping mid-number.
  // Lock resets on: clear input, dropdown country select.
  const setPhoneNumber = (value) => {
    const startsWithPlus = value.trimStart().startsWith('+');
    const stateHadPlus = phoneNumber.startsWith('+');
    const isInternational = startsWithPlus || stateHadPlus;

    let cleaned = value.replace(/\D/g, '');

    if (!cleaned && !startsWithPlus) {
      detectionLockedRef.current = false;
      setPhoneNumberInternal('');
      return;
    }

    if (isInternational) {
      if (cleaned.startsWith('0')) cleaned = cleaned.slice(1);

      const detected = detectCountry(cleaned, sortedForDetection);
      if (detected) {
        if (detected.country.code !== selectedCountry?.code) {
          setSelectedCountry(detected.country);
        }
        setPhoneNumberInternal(detected.nationalPart);
        detectionLockedRef.current = true;
      } else if (cleaned || startsWithPlus) {
        setPhoneNumberInternal('+' + cleaned);
      } else {
        setPhoneNumberInternal('');
      }
      return;
    }

    if (cleaned.startsWith('0') && cleaned.length > 1 && selectedCountry?.trunkPrefix) {
      detectionLockedRef.current = true;
      setPhoneNumberInternal(cleaned.slice(1));
      return;
    }

    if (!detectionLockedRef.current) {
      const detected = detectCountry(cleaned, sortedForDetection);
      if (detected) {
        if (detected.country.code !== selectedCountry?.code && detected.nationalPart.length >= 3) {
          if (!selectedCountry?.pattern.test(cleaned)) {
            setSelectedCountry(detected.country);
            setPhoneNumberInternal(detected.nationalPart);
            detectionLockedRef.current = true;
            return;
          }
        }
        if (detected.country.code === selectedCountry?.code) {
          setPhoneNumberInternal(detected.nationalPart);
          return;
        }
      }
    }

    setPhoneNumberInternal(cleaned);
  };

  // Handlers: Country selection (disabled if selector off)
  const onCountrySelect = enableCountrySelector
    ? (country) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
        setSearchTerm('');
        detectionLockedRef.current = false;
      }
    : () => {};

  // Handlers: Dropdown toggle (disabled if selector off)
  const onToggleDropdown = enableCountrySelector
    ? () => setIsDropdownOpen(prev => !prev)
    : () => {};

  // Effect: Validate phone number
  useEffect(() => {
    setIsValid(selectedCountry ? selectedCountry.pattern.test(phoneNumber) : false);
  }, [phoneNumber, selectedCountry]);

  // Effect: Notify parent on state updates
  useEffect(() => {
    if (selectedCountry) {
      onChange({ selectedCountry, phoneNumber, isValid });
    }
  }, [selectedCountry, phoneNumber, isValid, onChange]);

  // Ref: Dropdown container for click-outside detection
  const dropdownRef = useRef(null);

  // Effect: Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Effect: Reset search when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) setSearchTerm('');
  }, [isDropdownOpen]);

  // Return: State, handlers, and derived values
  return {
    selectedCountry,
    phoneNumber,
    setPhoneNumber,
    isValid,
    onCountrySelect,
    onToggleDropdown,
    filteredCountries,
    isDropdownOpen,
    searchTerm,
    setSearchTerm,
    dropdownRef,
  };
}
